import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faCircleCheck,
    faCheck,
    faGlobe,
    faUser,
    faPlus
} from "@fortawesome/free-solid-svg-icons";
import { FiServer, FiLink, FiTrash } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";

const initialFormData = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    url: "",
    ips: [""],
};

const steps = [
    {
        title: "Informations du demandeur",
        description: "Vos informations",
        heading: "Informations du demandeur",
        help: "Renseignez les coordonnées de la personne à contacter pour le scan.",
        icon: faUser,
    },
    {
        title: "Périmètre du site",
        description: "Cible et adresses IP",
        heading: "Périmètre du site",
        help: "Indiquez la cible principale et toutes les IP à inclure dans la demande.",
        icon: faGlobe,
    },
    {
        title: "Validation",
        description: "Récapitulatif",
        heading: "Validation de la demande",
        help: "Vérifiez le récapitulatif avant l’envoi pour signature.",
        icon: faCircleCheck,
    },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ipv4Regex =
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}(\/([0-9]|[1-2]\d|3[0-2]))?$/;
const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){2,7}[0-9a-fA-F]{1,4}|::1|::)(\/(12[0-8]|1[01]\d|\d{1,2}))?$/;

const isValidUrl = value => {
    try {
        const url = new URL(value);
        return ["http:", "https:"].includes(url.protocol);
    } catch {
        return false;
    }
};

const isValidIp = value => ipv4Regex.test(value.trim()) || ipv6Regex.test(value.trim());

export default function ScanForm({ api, onCreated, onViewRequests }) {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitLock = useRef(false);

    const cleanIps = useMemo(
        () => formData.ips.map(ip => ip.trim()).filter(Boolean),
        [formData.ips]
    );

    useEffect(() => {
        if (!user) {
            return;
        }

        setFormData(previous => ({
            ...previous,
            firstName: previous.firstName || user.firstName || "",
            lastName: previous.lastName || user.lastName || "",
            email: previous.email || user.email || user.username || "",
            company: previous.company || user.holdingName || user.entrepriseName || "",
        }));
    }, [user]);

    const updateField = (field, value) => {
        setFormData(previous => ({
            ...previous,
            [field]: value,
        }));
    };

    const updateIp = (index, value) => {
        setFormData(previous => ({
            ...previous,
            ips: previous.ips.map((ip, ipIndex) => (ipIndex === index ? value : ip)),
        }));
    };

    const addIp = () => {
        setFormData(previous => ({
            ...previous,
            ips: [...previous.ips, ""],
        }));
    };

    const removeIp = index => {
        setFormData(previous => ({
            ...previous,
            ips: previous.ips.length === 1
                ? [""]
                : previous.ips.filter((_, ipIndex) => ipIndex !== index),
        }));
    };

    const getStepErrors = stepIndex => {
        const nextErrors = {};

        if (stepIndex === 0) {
            if (!formData.firstName.trim()) {
                nextErrors.firstName = t("Le prenom est obligatoire.");
            }
            if (!formData.lastName.trim()) {
                nextErrors.lastName = t("Le nom est obligatoire.");
            }
            if (!emailRegex.test(formData.email.trim())) {
                nextErrors.email = t("Une adresse email valide est obligatoire.");
            }
        }

        if (stepIndex === 1) {
            if (!isValidUrl(formData.url.trim())) {
                nextErrors.url = t("Une URL valide en http ou https est obligatoire.");
            }
            if (cleanIps.length === 0) {
                nextErrors.ips = t("Ajoutez au moins une adresse IP.");
            } else if (!cleanIps.every(isValidIp)) {
                nextErrors.ips = t("Une ou plusieurs adresses IP ne sont pas valides.");
            }
        }

        return nextErrors;
    };

    const validateStep = stepIndex => {
        const nextErrors = getStepErrors(stepIndex);
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const goToNextStep = () => {
        if (!validateStep(currentStep)) {
            return;
        }
        setCurrentStep(previous => Math.min(previous + 1, steps.length - 1));
    };

    const goToPreviousStep = () => {
        setErrors({});
        setCurrentStep(previous => Math.max(previous - 1, 0));
    };

    const handleSubmit = event => {
        event.preventDefault();
    };

    const handleValidate = async () => {
        if (submitLock.current) return;

        const requesterErrors = getStepErrors(0);
        const targetErrors = getStepErrors(1);

        if (Object.keys(requesterErrors).length > 0) {
            setCurrentStep(0);
            setErrors(requesterErrors);
            return;
        }

        if (Object.keys(targetErrors).length > 0) {
            setCurrentStep(1);
            setErrors(targetErrors);
            return;
        }

        const payload = {
            data_scan: {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                company: formData.company.trim(),
                url: formData.url.trim(),
                ips: cleanIps,
            },
        };

        submitLock.current = true;
        setIsSubmitting(true);
        try {
            const request = await api.create(payload.data_scan);
            setErrors({});
            onCreated?.(request);
        } catch (error) {
            // Même si l'envoi DocuSign n'a pas pu être confirmé, la demande
            // peut déjà exister côté backend : on bascule donc vers le suivi.
            onViewRequests?.(error);
        } finally {
            submitLock.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <section className="scan-wizard" aria-labelledby="scan-wizard-title">
            <div className="scan-layout">
                <header className="scan-wizard-heading">
                    <div>
                        <h2 id="scan-wizard-title">{t("Nouvelle demande de scan")}</h2>
                        <p>{t("Configurez votre demande en 3 étapes")}</p>
                    </div>
                </header>
                <ol className="scan-wizard-steps" aria-label={t("Étapes de la demande")}>
                    {steps.map((step, index) => (
                        <li
                            key={step.title}
                            className={index < currentStep || (isSubmitting && index === steps.length - 1) ? "is-complete" : index === currentStep ? "is-current" : ""}
                            aria-current={!isSubmitting && index === currentStep ? "step" : undefined}
                        >
                            <span className="scan-wizard-step-number" aria-hidden="true">
                                {index < currentStep || (isSubmitting && index === steps.length - 1) ? <FontAwesomeIcon icon={faCheck} /> : index + 1}
                            </span>
                            <span className="scan-wizard-step-copy">
                                <strong>{index + 1}. {t(step.title)}</strong>
                                <small>{t(step.description)}</small>
                            </span>
                        </li>
                    ))}
                </ol>
                <form className="scan-form" onSubmit={handleSubmit}>
                    <fieldset className="scan-submit-fields" disabled={isSubmitting}>
                        {currentStep === 0 && (
                            <div className="scan-form-panel">
                                <div className="scan-form-grid">
                                    <label className="scan-field">
                                        <span>{t("Prénom")} <span className="scan-field-required">*</span></span>
                                        <input
                                            type="text"
                                            autoComplete="given-name"
                                            aria-required="true"
                                            aria-invalid={Boolean(errors.firstName)}
                                            value={formData.firstName}
                                            onChange={event => updateField("firstName", event.target.value)}
                                            placeholder={t("Ex. : Léa")}
                                        />
                                        {errors.firstName && <small className="scan-error">{errors.firstName}</small>}
                                    </label>

                                    <label className="scan-field">
                                        <span>{t("Nom")} <span className="scan-field-required">*</span></span>
                                        <input
                                            type="text"
                                            autoComplete="family-name"
                                            aria-required="true"
                                            aria-invalid={Boolean(errors.lastName)}
                                            value={formData.lastName}
                                            onChange={event => updateField("lastName", event.target.value)}
                                            placeholder={t("Ex: Martin")}
                                        />
                                        {errors.lastName && <small className="scan-error">{errors.lastName}</small>}
                                    </label>

                                    <label className="scan-field">
                                        <span>{t("Adresse email")} <span className="scan-field-required">*</span></span>
                                        <input
                                            type="email"
                                            autoComplete="email"
                                            aria-required="true"
                                            aria-invalid={Boolean(errors.email)}
                                            value={formData.email}
                                            onChange={event => updateField("email", event.target.value)}
                                            placeholder={t("prenom.nom@entreprise.fr")}
                                        />
                                        {errors.email && <small className="scan-error">{errors.email}</small>}
                                    </label>

                                    <label className="scan-field">
                                        <span>{t("Entreprise")}</span>
                                        <input
                                            type="text"
                                            value={formData.company}
                                            readOnly
                                            placeholder={t("Nom de l'entreprise")}
                                        />
                                    </label>
                                </div>
                            </div>
                        )}

                        {currentStep === 1 && (
                            <div className="scan-form-panel">
                                <div className="scan-form-grid">
                                    <label className="scan-field scan-field-wide">
                                        <span>{t("URL du site")} <span className="scan-field-required">*</span></span>
                                        <span className="scan-input-with-icon">
                                            <FiLink />
                                            <input
                                                type="url"
                                                aria-required="true"
                                                aria-invalid={Boolean(errors.url)}
                                                value={formData.url}
                                                onChange={event => updateField("url", event.target.value)}
                                                placeholder={t("https://www.exemple.fr")}
                                            />
                                        </span>
                                        {errors.url && <small className="scan-error">{errors.url}</small>}
                                    </label>
                                </div>

                                <div className="scan-ip-section">
                                    <div className="scan-ip-section-header">
                                        <div className="scan-ip-section-title">
                                            <div>
                                                <h4>{t("Adresses IP")}</h4>
                                                <p>{t("Ajoutez une ou plusieurs IP, avec CIDR si nécessaire.")}</p>
                                            </div>
                                        </div>
                                        <button type="button" className="scan-form-button scan-form-button--quaternary " onClick={addIp}>
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    </div>

                                    <div className="scan-ip-list">
                                        {formData.ips.map((ip, index) => (
                                            <div className="scan-ip-row" key={`ip-${index}`}>
                                                <div className="scan-input-with-icon">
                                                    <FiServer />
                                                    <input
                                                        type="text"
                                                        aria-label={t("Adresse IP {{number}}", { number: index + 1 })}
                                                        aria-invalid={Boolean(errors.ips)}
                                                        value={ip}
                                                        onChange={event => updateIp(index, event.target.value)}
                                                        placeholder={t("Ex: 192.168.1.10 ou 2001:db8::1")}
                                                    />
                                                    <span
                                                        className="scan-icon-button"
                                                        onClick={() => removeIp(index)}
                                                        aria-label={t("Supprimer cette IP")}
                                                    >
                                                        <FiTrash />
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {errors.ips && <small className="scan-error">{errors.ips}</small>}
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="scan-form-panel">
                                <div className="scan-summary">
                                    <div>
                                        <span>{t("Demandeur")}</span>
                                        <strong>{`${formData.firstName || "-"} ${formData.lastName || ""}`}</strong>
                                        <small>{formData.email || "-"}</small>
                                    </div>
                                    <div>
                                        <span>{t("URL du site")}</span>
                                        <strong>{formData.url || "-"}</strong>
                                        <small>{t("URL renseignée")}</small>
                                    </div>
                                    <div>
                                        <span>{t("IP incluses")}</span>
                                        <strong>{cleanIps.length}</strong>
                                        <small>{cleanIps.join(", ") || "-"}</small>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="scan-form-actions">
                            <button
                                type="button"
                                className="scan-form-button scan-form-button--secondary"
                                onClick={goToPreviousStep}
                                disabled={currentStep === 0}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                                {t("Retour")}
                            </button>

                            {currentStep < steps.length - 1 ? (
                                <button type="button" className="scan-form-button scan-form-button--primary" onClick={goToNextStep}>
                                    {t("Continuer")}
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            ) : (
                                <button type="button" className="scan-form-button scan-form-button--primary" onClick={handleValidate}>
                                    <FontAwesomeIcon icon={faCircleCheck} />
                                    {t(isSubmitting ? "Envoi en cours…" : "Envoyer pour signature")}
                                </button>
                            )}
                        </div>

                    </fieldset>
                </form>
            </div>
        </section>
    );
}
